import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SmartBookingLayout } from '../components/SmartBookingLayout';
import { StepCard } from '../components/StepCard';
import { SyncLoader } from 'react-spinners';
import { RootState } from '../redux/store';
import { 
  setPatientDetails, 
  setPatientRequestId, 
  setTreatment, 
  setPatientType, 
  setSelectedSlot, 
  setSelectedOperatories 
} from '../redux/slices/appointmentSlice';
import { Button } from '../components/Button';
import TimeSlotSelection from '../components/TimeSlotSelection';
import Toggle from '../components/Toggle';
import { 
  searchPatientDetails, 
  fetchAvailableSlots, 
  createPatient,
  AvailableSlotsResponse 
} from '../services/api';
import { formatDateToISO } from '../utils/helpers';
import { PATIENT_TYPE_TO_BACKEND, PatientTypeEnum } from '../types';

type SlotsByDate = Record<string, Record<string, string[]>>;

const ERROR_MESSAGES = {
  INVALID_APPOINTMENT_TYPE: 'Invalid appointment_type. Please check the link and try again.',
  PATIENT_LOAD_FAILED: 'Failed to load patient information',
  PATIENT_SAVE_FAILED: 'Failed to save patient. Please try again.',
  CONTACT_SUPPORT: 'Please contact support if you need assistance.',
} as const;

export const SmartBooking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { practice, token } = useSelector((s: RootState) => s.auth);
  const { treatment, patientType, selectedSlot, selectedDurationMinutes, patientDetails } = useSelector(
    (s: RootState) => s.appointment
  );
  
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [savingPatient, setSavingPatient] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slotsByDate, setSlotsByDate] = useState<SlotsByDate>({});
  const [currentStart, setCurrentStart] = useState(new Date());

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const appointmentTypeSlug = params.get('appointment_type');
  const patientId = params.get('patient_id');

  const currentEnd = useMemo(() => {
    const date = new Date(currentStart);
    date.setDate(date.getDate() + 6);
    return date;
  }, [currentStart]);

  // Initialize appointment type and patient details
  useEffect(() => {
    const initialize = async () => {
      if (!token || !practice) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Validate and set appointment type
        if (appointmentTypeSlug) {
          const matchedAppointmentType = practice.appointment_types?.find(
            (at) => at.slug?.toLowerCase() === appointmentTypeSlug.toLowerCase()
          );

          if (!matchedAppointmentType) {
            setError(ERROR_MESSAGES.INVALID_APPOINTMENT_TYPE);
            return;
          }

          dispatch(setTreatment(matchedAppointmentType));
        }

        // Load patient details if patient_id exists
        if (patientId) {
          await loadPatientDetails(patientId);
        }
      } catch (e: any) {
        setError(e?.message || ERROR_MESSAGES.PATIENT_LOAD_FAILED);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [token, practice, appointmentTypeSlug, patientId]);

  // Load patient details
  const loadPatientDetails = async (encryptedId: string) => {
    const response = await searchPatientDetails({ patient_encrypted_id: encryptedId });
    const patientData = response.data.attributes;

    dispatch(setPatientType(PATIENT_TYPE_TO_BACKEND.existing as PatientTypeEnum));
    dispatch(setPatientDetails({
      firstName: patientData.first_name || '',
      lastName: patientData.last_name || '',
      email: patientData.email || '',
      phone: patientData.mobile_phone || '',
      id: response.data.id,
      external_resource_id: patientData.external_resource_id,
      notes: '',
    }));
  };

  // Fetch available slots
  useEffect(() => {
    const loadSlots = async () => {
      if (!treatment || !patientType || !selectedDurationMinutes) {
        setSlotsByDate({});
        return;
      }

      setLoadingSlots(true);
      try {
        const response: AvailableSlotsResponse = await fetchAvailableSlots({
          start_date: formatDateToISO(currentStart),
          end_date: formatDateToISO(currentEnd),
          new_patient: patientType === 'new',
          fixed_duration_slots: true,
          appointment_type_id: treatment.id,
        });

        const processedSlots = processSlotsResponse(response);
        setSlotsByDate(processedSlots);
      } catch (e) {
        console.error('Failed to load slots:', e);
        setSlotsByDate({});
      } finally {
        setLoadingSlots(false);
      }
    };

    loadSlots();
  }, [currentStart, currentEnd, treatment, patientType, selectedDurationMinutes]);

  // Process slots response into organized structure
  const processSlotsResponse = (response: AvailableSlotsResponse): SlotsByDate => {
    const slotMap: SlotsByDate = {};

    for (const operatory of response || []) {
      const operatoryId = operatory?.id;
      const availableSlots = operatory?.attributes?.available_slots || {};

      for (const [dateKey, slots] of Object.entries(availableSlots)) {
        if (!slotMap[dateKey]) {
          slotMap[dateKey] = {};
        }

        for (const slot of slots || []) {
          const timeDisplay = slot.start_time;
          if (!slotMap[dateKey][timeDisplay]) {
            slotMap[dateKey][timeDisplay] = [];
          }
          slotMap[dateKey][timeDisplay].push(operatoryId);
        }
      }
    }

    return slotMap;
  };

  // Navigation handlers
  const goPrevWeek = useCallback(() => {
    setCurrentStart((prev) => {
      const date = new Date(prev);
      date.setDate(date.getDate() - 7);
      return date;
    });
  }, []);

  const goNextWeek = useCallback(() => {
    setCurrentStart((prev) => {
      const date = new Date(prev);
      date.setDate(date.getDate() + 7);
      return date;
    });
  }, []);

  // Save patient and proceed
  const savePatient = async () => {
    if (!selectedSlot) return;

    setSavingPatient(true);
    try {
      const payload = {
        first_name: patientDetails.firstName,
        last_name: patientDetails.lastName,
        email: patientDetails.email,
        phone_number: patientDetails.phone,
        patient_type: patientType,
        patient_id: patientDetails.id,
        external_resource_id: patientDetails.external_resource_id,
      };

      const patientRequest = await createPatient(payload);
      dispatch(setPatientRequestId(patientRequest.request_id));
      
      // Navigate to confirmation
      const paramsClone = new URLSearchParams(location.search);
      paramsClone.set('smart_booking', 'true');
      navigate(`/bookonline/confirmation?${paramsClone.toString()}`);
    } catch (error) {
      setError(ERROR_MESSAGES.PATIENT_SAVE_FAILED);
    } finally {
      setSavingPatient(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <SmartBookingLayout>
        <div className="flex items-center justify-center min-h-[40vh]">
          <SyncLoader />
        </div>
      </SmartBookingLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <SmartBookingLayout>
        <StepCard>
          <div className="flex items-center justify-center min-h-[40vh] text-center">
            <div>
              <p className="text-lg font-medium text-red-600 mb-2">{error}</p>
              <p className="text-sm text-gray-500">{ERROR_MESSAGES.CONTACT_SUPPORT}</p>
            </div>
          </div>
        </StepCard>
      </SmartBookingLayout>
    );
  }

  const hasSlots = Object.keys(slotsByDate).length > 0;

  return (
    <SmartBookingLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-black">When would you like to come?</h1>
            <p className="text-sm text-gray-500">
              Convenient evenings and weekends to fit your schedule
            </p>
          </div>
          {practice?.promote_primetime && (
            <div className="pr-2">
              <Toggle />
            </div>
          )}
        </div>

        {/* Time Slots */}
        <StepCard>
          {loadingSlots ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <SyncLoader />
            </div>
          ) : hasSlots ? (
            <TimeSlotSelection
              selectedSlot={selectedSlot}
              onSlotSelect={(slot) => dispatch(setSelectedSlot(slot))}
              onOperatoriesSelect={(ops) => dispatch(setSelectedOperatories(ops))}
              slotsByDate={slotsByDate}
              onPrevWeek={goPrevWeek}
              onNextWeek={goNextWeek}
              currentStart={currentStart}
            />
          ) : (
            <div className="flex items-center justify-center min-h-[40vh] text-center">
              <div>
                <p className="text-lg font-medium text-gray-800">
                  No available slots for this practice.
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Please contact admin for support.
                </p>
              </div>
            </div>
          )}
        </StepCard>

        {/* Continue Button */}
        <div className="flex justify-end">
          <Button
            title={savingPatient ? 'Saving...' : 'Continue'}
            disabled={!selectedSlot || savingPatient}
            onClick={savePatient}
          />
        </div>
      </div>
    </SmartBookingLayout>
  );
};
